<template>
  <v-dialog
    v-model="dialog"
    width="768"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        :text="textBtn"
        :color="color"
        rounded
      >
        <v-icon v-if="icon" :left="shiftIconLeft">
          {{ icon }}
        </v-icon>
        <span v-if="methodTitle">{{ methodTitle }}</span>
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <v-row class="d-flex justify-space-between align-center">
          <v-col class="grow">
            <span class="headline">
              {{ methodTitle }}
            </span>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col>
              <v-select
                v-model="type"
                :items="['Temporary', 'Permanent']"
                prepend-icon="mdi-shape"
                label="Type"
              ></v-select>
            </v-col>
            <v-col>
              <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="date"
                    label="Validation Date"
                    prepend-icon="event"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker 
                  v-model="date" 
                  no-title 
                  @input="dateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col v-if="type !== 'Permanent'">
              <v-autocomplete
                v-model="result"
                :items="resultOptions"
                prepend-icon="mdi-thumbs-up-down"
                label="Result"
                required
                autocomplete="new-password"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="d-flex align-center justify-space-between">
            <v-col>
              <v-autocomplete
                v-model="employee"
                :items="employeeOptions"
                prepend-icon="mdi-account"
                label="Employee"
                required
                autocomplete="new-password"
                clearable
              >
                <template v-slot:item="data">
                  <template>
                    <v-list-item-avatar>
                      <v-img :src="getAvatar(data.item.avatar, data.item.text)"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.text }}
                        <v-chip 
                          v-if="data.item.inactive" 
                          color="error" 
                          class="text-uppercase"
                          x-small
                        >Inactive</v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ data.item.subtext }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col class="shrink">
              OR
            </v-col>
            <v-col>
              <v-text-field 
                v-model="name"
                label="Name"
                prepend-icon="short_text"
                hint="Only use this field if the person is not an employee"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="d-flex justify-space-between">
            <v-col cols="8">
              <v-btn
                v-if="showDeleteButton"
                color="error"
                rounded
                text
                :disabled="!enableSaveButton"
                @click="toggleShowDeleteConfirmation"
              >
                Delete Test
              </v-btn>
              <v-btn 
                v-if="showDeleteConfirmationButton"
                color="error"
                class="mr-5"
                rounded
                @click="deleteTest"
                :loading="deleteLoading"
              >
                Really Delete Test?
              </v-btn>
              <v-btn 
                v-if="showDeleteConfirmationButton"
                text
                rounded
                @click="toggleShowDeleteConfirmation"
              >
                Nevermind
              </v-btn>
            </v-col>
            <v-col class="d-flex justify-end">
              <v-btn 
                text
                :disabled="saveLoading"
                @click="close"
                rounded
              >Cancel</v-btn>
              <v-btn 
                color="primary"
                text
                rounded
                :disabled="!enableSaveButton"
                :loading="saveLoading"
                @click="save"
              >Save</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    name: 'TestForm',
    props: {
      method: String,
      test: Object,
      methodTitle: String,
      icon: String,
      textBtn: Boolean,
      color: String
    },
    data () {
      return {
        type: 'Temporary',
        date: '',
        employee: null,
        name: '',
        result: '',
        dateMenu: false,
        resultOptions: ['Cleared', 'Not Cleared'],
        saveLoading: false,
        dialog: false,
        showSuccessMsg: false,
        successMsg: '',
        showDeleteConfirmationButton: false,
        deleteLoading: false,
      }
    },
    computed: {
      employees () {
        return this.$store.getters.getEmployees
      },
      employeeOptions () {
        return this.employees.slice(0).sort((a, b) => {
          return a.fields.Name > b.fields.Name ? 1 : -1
        }).map(e => {
          return {
            text: e.fields.Name,
            subtext: `${e.fields.Title} • ${e.fields.Department}`,
            inactive: e.fields.Status === 'Inactive',
            avatar: e.fields.Avatar ? 
                    e.fields.Avatar[0].url :
                    null,
            value: e.id
          }
        })
      },
      shiftIconLeft () {
        return this.icon !== undefined
      },
      showDeleteButton () {
        return !this.showDeleteConfirmationButton && this.method === 'edit'
      },
      enableSaveButton () {
        const hasEmployee = this.employee !== '' || this.name !== ''
        const isPermanent = this.type == 'Permanent'
        const hasDate = this.date !== ''
        const isNotLoading = !this.saveLoading

        return hasEmployee && ((isPermanent && hasDate) || hasDate) && isNotLoading
      }
    },
    created () {
      this.init()
    },
    watch: {
      test (newVal, oldVal) {
        if (newVal && oldVal && newVal.id !== oldVal.id) {
          this.init()
        }
      }
    },
    methods: {
      init () {
        if (this.test) {
          this.date = this.test.fields.Date
          this.result = this.test.fields.Result
          this.employee = this.test.employee ? this.test.employee.id : null
          this.name = this.test.fields.Name
        }
        this.$store.dispatch('getRecords', {
          table: 'Tests',
          view: 'All Tests',
          dispatch: 'setTests'
        })
      },
      getAvatar (avatar, name) {
        if (!avatar || avatar === undefined) {
          const color = this.$vuetify.theme.themes.light.primary.split('#')[1]
          return `https://ui-avatars.com/api/?name=${name}&background=${color}&color=fff&rounded=true`
        } else {
          return avatar
        }
      },
      save () {
        this.saveLoading = true
        let fields = {
          'Type': this.type,
          'Date': this.date,
          'Result': 'Cleared',
          'Non-Employee': this.name
        }
        if (this.employee) {
          fields['Employee'] = [this.employee]
        }
        if (this.name && !this.employee) {
          fields['Employee'] = []
        }
        if (this.method === 'create') {
          this.$store.dispatch('createRecords', {
            table: 'Tests',
            records: [{ fields: fields }]
          })
          .then((values) => {
            this.$store.dispatch('addTest', values[0].data[0])
            this.$store.dispatch('setSnackbar', {
              snackbarColor: 'success',
              showSnackbar: true,
              snackbarY: 'top',
              snackbarX: 'right',
              snackbarText: `Created clearance`
            })
            this.saveLoading = false
            this.close()
          })
        } else if (this.method === 'edit') {
          this.$store.dispatch('updateRecords', {
            table: 'Tests',
            records: [{ id: this.test.id, fields: fields }]
          })
          .then(() => {
            this.$store.dispatch('getRecords', {
              table: 'Tests',
              view: 'All Tests',
              dispatch: 'setTests'
            })
            .then(() => {
              this.$store.dispatch('setSnackbar', {
                snackbarColor: 'success',
                showSnackbar: true,
                snackbarY: 'top',
                snackbarX: 'right',
                snackbarText: `Updated clearance`
              })
              this.saveLoading = false
              this.close()
            })
          })
        }
      },
      deleteTest () {
        this.deleteLoading = true
        this.$store.dispatch('deleteRecords', {
          table: 'Tests',
          records: [this.test]
        })
        .then(() => {
          this.showDeleteConfirmationButton = false
          this.deleteLoading = false
          this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: `Deleted clearance`
          })
          this.init()
          this.close()
        })
      },
      close () {
        this.dialog = false
        this.showDeleteConfirmationButton = false
      },
      clearForm () {
        this.date = ''
        this.result = ''
        this.name = ''
      },
      toggleShowDeleteConfirmation () {
        return this.showDeleteConfirmationButton = 
               !this.showDeleteConfirmationButton
      }
    }
  }
</script>
